// Racket Lab R4 — internal Calendar Link scheduling dialog (Feb 2026).
//
// Compact modal to create an internal `calendar_events` entry linked to
// a service ticket. NO Google Calendar, NO ICS, NO invitations — just
// the local time window the technician wants to commit. Server forces
// title="Racket Lab — {racket_model}", category="meeting", color="violet".
//
// Privacy notes:
//   • The dialog never renders the player's email or name. All identifiers
//     remain UUIDs handled server-side.
//   • Failure paths surface backend error codes via `errors.*` toasts so
//     no raw stack trace leaks.
import React, { useState } from "react";
import { api, extractErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarPlus } from "lucide-react";

// Defaults: tomorrow 10:00 → 11:00 local time, rendered as ISO with TZ.
const _defaultRange = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(10, 0, 0, 0);
  const end = new Date(tomorrow.getTime() + 60 * 60 * 1000);
  const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  return { startsAt: toLocalInput(tomorrow), endsAt: toLocalInput(end) };
};

export default function SchedulingDialog({ open, onOpenChange, ticket, t, onLinked }) {
  const { startsAt: initStart, endsAt: initEnd } = _defaultRange();
  const [startsAt, setStartsAt] = useState(initStart);
  const [endsAt, setEndsAt] = useState(initEnd);
  const [location, setLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Convert datetime-local (no TZ) to an ISO with the browser's offset
      // so the backend Pydantic dateparse keeps the user's intent.
      const toIso = (s) => new Date(s).toISOString();
      const body = {
        starts_at: toIso(startsAt),
        ends_at: toIso(endsAt),
        location: location.trim() || null,
      };
      const r = await api.post(`/racket-lab/service-tickets/${ticket.id}/calendar`, body);
      toast.success(t("racketLab.tickets.schedulingCreated"));
      onLinked?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, t("racketLab.tickets.schedulingFail")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="racket-lab-scheduling-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="h-4 w-4 text-violet-600" />
            {t("racketLab.tickets.schedulingDialogTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("racketLab.tickets.schedulingDialogDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="r4-starts" className="text-xs">
              {t("racketLab.tickets.schedulingStartsAt")}
            </Label>
            <Input
              id="r4-starts"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              data-testid="racket-lab-scheduling-starts"
            />
          </div>
          <div>
            <Label htmlFor="r4-ends" className="text-xs">
              {t("racketLab.tickets.schedulingEndsAt")}
            </Label>
            <Input
              id="r4-ends"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              data-testid="racket-lab-scheduling-ends"
            />
          </div>
          <div>
            <Label htmlFor="r4-location" className="text-xs">
              {t("racketLab.tickets.schedulingLocation")}
            </Label>
            <Input
              id="r4-location"
              type="text"
              maxLength={200}
              placeholder={t("racketLab.tickets.schedulingLocationPlaceholder")}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              data-testid="racket-lab-scheduling-location"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="racket-lab-scheduling-cancel"
          >
            {t("racketLab.tickets.schedulingCancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !startsAt || !endsAt}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="racket-lab-scheduling-submit"
          >
            {submitting ? "…" : t("racketLab.tickets.schedulingSubmit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
